import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import axios from "axios";

const defaultDraft = {
  id: null,
  key: "",
  name: "",
  category: "",
  enabled: true,
  subjectHtml: "",
  bodyHtml: "",
};

const defaultSampleVars = {
  "customer.name": "Acme Corp",
  "order.number": "SO-10421",
  "user.name": "Dallas",
  customerName: "Alex Customer",
  companyName: "Sample Company",
  contactEmail: "alex.customer@example.com",
  contactPhone: "555-0100",
  cardTitle: "Sample opportunity",
  crmStage: "Estimate Sent",
  ownerName: "Sales Rep",
  orderNumber: "SO-10421",
  prospectReference: "PROS-451",
  estimateNumber: "EST-2209",
  lastActivityDate: "2026-07-10",
  nextActivityDate: "2026-07-18",
  trackingReference: "[EL-CRM-PROSPECT-123]",
  instanceName: "Sample Instance",
};

const CATEGORY_OPTIONS = [
  "Presentations",
  "Orders",
  "Purchasing",
  "Quotes",
  "CRM Follow-up",
  "CRM Estimate Follow-up",
  "CRM Presentation Follow-up",
  "CRM Stale Opportunity",
  "CRM Re-engagement",
  "CRM Lost Opportunity Reopen",
  "CRM Meeting Reminder",
  "CRM Internal Rep Reminder",
];

const CRM_CATEGORIES = new Set(CATEGORY_OPTIONS.filter((category) => category.startsWith("CRM ")));
const DOCUMENT_CATEGORIES = new Set(["Presentations", "Orders", "Purchasing", "Quotes"]);
const CRM_TYPE_OPTIONS = [
  { category: "CRM Follow-up", label: "CRM Follow-up", key: "crm-follow-up", name: "CRM follow-up" },
  { category: "CRM Estimate Follow-up", label: "Estimate Follow-up", key: "crm-estimate-follow-up", name: "CRM estimate follow-up" },
  { category: "CRM Presentation Follow-up", label: "Presentation Follow-up", key: "crm-presentation-follow-up", name: "CRM presentation follow-up" },
  { category: "CRM Stale Opportunity", label: "Stale Opportunity", key: "crm-stale-opportunity", name: "CRM stale opportunity" },
  { category: "CRM Re-engagement", label: "Re-engagement", key: "crm-re-engagement", name: "CRM re-engagement" },
  { category: "CRM Lost Opportunity Reopen", label: "Lost Opportunity Reopen", key: "crm-lost-opportunity-reopen", name: "CRM lost opportunity reopen" },
  { category: "CRM Meeting Reminder", label: "Meeting Reminder", key: "crm-meeting-reminder", name: "CRM meeting reminder" },
  { category: "CRM Internal Rep Reminder", label: "Internal Rep Reminder", key: "crm-internal-rep-reminder", name: "CRM internal rep reminder" },
];
const CRM_TYPE_BY_CATEGORY = CRM_TYPE_OPTIONS.reduce((acc, option) => ({ ...acc, [option.category]: option }), {});
const customCrmKey = (option) => `${option.key}-custom-${Date.now()}`;
const CRM_STARTER_CONTENT = {
  "CRM Follow-up": {
    subjectHtml: "Following up on {{cardTitle}}",
    bodyHtml: "<p>Hi {{customerName}},</p><p>I wanted to follow up on {{cardTitle}}. Please let me know if you have any questions or if there is anything else we can provide.</p><p>Thanks,<br/>{{ownerName}}</p><p style=\"font-size:12px;color:#666;\">Reference: {{trackingReference}}</p>",
  },
  "CRM Estimate Follow-up": {
    subjectHtml: "Estimate follow-up for {{cardTitle}}",
    bodyHtml: "<p>Hi {{customerName}},</p><p>I am checking in on the estimate for {{cardTitle}}. We are happy to review options, timing, or any changes you would like to make.</p><p>Thanks,<br/>{{ownerName}}</p><p style=\"font-size:12px;color:#666;\">Reference: {{trackingReference}}</p>",
  },
  "CRM Presentation Follow-up": {
    subjectHtml: "Presentation follow-up for {{cardTitle}}",
    bodyHtml: "<p>Hi {{customerName}},</p><p>I wanted to follow up on the presentation for {{cardTitle}} and see what feedback or next steps you have in mind.</p><p>Thanks,<br/>{{ownerName}}</p><p style=\"font-size:12px;color:#666;\">Reference: {{trackingReference}}</p>",
  },
  "CRM Stale Opportunity": {
    subjectHtml: "Checking in on {{cardTitle}}",
    bodyHtml: "<p>Hi {{customerName}},</p><p>I wanted to check whether {{cardTitle}} is still active on your side. If timing has changed, we can adjust the next steps.</p><p>Thanks,<br/>{{ownerName}}</p>",
  },
  "CRM Re-engagement": {
    subjectHtml: "Reconnecting about {{companyName}}",
    bodyHtml: "<p>Hi {{customerName}},</p><p>It has been a while since we connected. If {{companyName}} has any upcoming projects, we would be glad to help.</p><p>Thanks,<br/>{{ownerName}}</p>",
  },
  "CRM Lost Opportunity Reopen": {
    subjectHtml: "Revisiting {{cardTitle}}",
    bodyHtml: "<p>Hi {{customerName}},</p><p>I wanted to revisit {{cardTitle}} in case priorities, timing, or requirements have changed. We can reopen the conversation whenever it is useful.</p><p>Thanks,<br/>{{ownerName}}</p>",
  },
  "CRM Meeting Reminder": {
    subjectHtml: "Reminder: {{cardTitle}}",
    bodyHtml: "<p>Hi {{customerName}},</p><p>This is a quick reminder about our next step for {{cardTitle}}. Current CRM stage: {{crmStage}}.</p><p>Thanks,<br/>{{ownerName}}</p>",
  },
  "CRM Internal Rep Reminder": {
    subjectHtml: "Internal reminder: {{cardTitle}}",
    bodyHtml: "<p>Reminder for {{ownerName}}:</p><p>{{cardTitle}} is currently in {{crmStage}}. Next activity date: {{nextActivityDate}}.</p><p>Tracking reference: {{trackingReference}}</p>",
  },
};
const CRM_MERGE_FIELDS = [
  { key: "customerName", label: "Customer name", example: "{{customerName}}" },
  { key: "companyName", label: "Company", example: "{{companyName}}" },
  { key: "contactEmail", label: "Contact email", example: "{{contactEmail}}" },
  { key: "contactPhone", label: "Contact phone", example: "{{contactPhone}}" },
  { key: "cardTitle", label: "CRM card title", example: "{{cardTitle}}" },
  { key: "crmStage", label: "CRM stage", example: "{{crmStage}}" },
  { key: "ownerName", label: "Owner", example: "{{ownerName}}" },
  { key: "orderNumber", label: "Order number", example: "{{orderNumber}}" },
  { key: "prospectReference", label: "Prospect reference", example: "{{prospectReference}}" },
  { key: "estimateNumber", label: "Estimate number", example: "{{estimateNumber}}" },
  { key: "lastActivityDate", label: "Last activity", example: "{{lastActivityDate}}" },
  { key: "nextActivityDate", label: "Next activity", example: "{{nextActivityDate}}" },
  { key: "trackingReference", label: "Tracking reference", example: "{{trackingReference}}" },
  { key: "instanceName", label: "Instance name", example: "{{instanceName}}" },
];

const renderTemplate = (html, vars = {}) => {
  if (!html) return "";
  return String(html).replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_match, key) => {
    const val = vars[key];
    return val === undefined || val === null ? "" : String(val);
  });
};

export default function EmailTemplatesTab({ instanceUniqueId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(defaultDraft);
  const [sampleVars, setSampleVars] = useState(defaultSampleVars);
  const [instanceId, setInstanceId] = useState(null);
  const [templateView, setTemplateView] = useState("all");
  const [starterChoice, setStarterChoice] = useState("blank");

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchTemplates = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${SERVER}/instances/${targetInstanceId}/email-templates`,
        headers
      );
      setTemplates(res.data.templates || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) {
      fetchTemplates(instanceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const openCreate = () => {
    setDraft(defaultDraft);
    setStarterChoice("blank");
    setOpen(true);
  };

  const openCreateCrm = () => {
    const option = CRM_TYPE_OPTIONS[0];
    setDraft({
      ...defaultDraft,
      key: customCrmKey(option),
      name: `Custom ${option.name}`,
      category: option.category,
    });
    setStarterChoice("blank");
    setOpen(true);
  };

  const openEdit = (template) => {
    setDraft({
      id: template.id,
      key: template.key || "",
      name: template.name || "",
      category: template.category || "",
      enabled: !!template.enabled,
      subjectHtml: template.subjectHtml || "",
      bodyHtml: template.bodyHtml || "",
    });
    setStarterChoice("blank");
    setOpen(true);
  };

  const duplicate = (template) => {
    setDraft({
      id: null,
      key: `${template.key}-copy`,
      name: `${template.name} (Copy)`,
      category: template.category || "",
      enabled: template.enabled !== false,
      subjectHtml: template.subjectHtml || "",
      bodyHtml: template.bodyHtml || "",
    });
    setStarterChoice("blank");
    setOpen(true);
  };

  const selectCrmCategory = (category) => {
    const option = CRM_TYPE_BY_CATEGORY[category];
    setDraft((current) => ({
      ...current,
      category,
      ...(!current.id && option && (!current.key || CRM_TYPE_OPTIONS.some((entry) => current.key.startsWith(`${entry.key}-custom-`))) ? {
        key: customCrmKey(option),
        name: current.name || `Custom ${option.name}`,
      } : {}),
    }));
  };

  const applyStarterContent = () => {
    const starter = CRM_STARTER_CONTENT[draft.category];
    if (!starter || draft.id) return;
    setDraft((current) => ({
      ...current,
      subjectHtml: starter.subjectHtml,
      bodyHtml: starter.bodyHtml,
    }));
  };

  const insertMergeField = (field, target) => {
    const token = `{{${field}}}`;
    const key = target === "subject" ? "subjectHtml" : "bodyHtml";
    setDraft((current) => ({
      ...current,
      [key]: current[key] ? `${current[key]} ${token}` : token,
    }));
  };

  const save = async () => {
    const payload = {
      key: draft.key.trim(),
      name: draft.name.trim(),
      category: draft.category.trim() || null,
      enabled: !!draft.enabled,
      subjectHtml: draft.subjectHtml,
      bodyHtml: draft.bodyHtml,
    };

    if (!payload.key || !payload.name || !instanceId) return;

    if (draft.id) {
      await axios.put(
        `${SERVER}/instances/${instanceId}/email-templates/${draft.id}`,
        payload,
        headers
      );
    } else {
      await axios.post(
        `${SERVER}/instances/${instanceId}/email-templates`,
        payload,
        headers
      );
    }

    setOpen(false);
    fetchTemplates(instanceId);
  };

  const archive = async (template) => {
    if (!instanceId) return;
    await axios.delete(
      `${SERVER}/instances/${instanceId}/email-templates/${template.id}`,
      headers
    );
    fetchTemplates(instanceId);
  };

  const renderedSubject = useMemo(
    () => renderTemplate(draft.subjectHtml, sampleVars),
    [draft.subjectHtml, sampleVars]
  );
  const renderedBody = useMemo(
    () => renderTemplate(draft.bodyHtml, sampleVars),
    [draft.bodyHtml, sampleVars]
  );
  const isCrmTemplate = CRM_CATEGORIES.has(String(draft.category || "").trim());
  const filteredTemplates = useMemo(() => {
    if (templateView === "crm") {
      return (templates || []).filter((template) => CRM_CATEGORIES.has(String(template.category || "").trim()));
    }
    if (templateView === "documents") {
      return (templates || []).filter((template) => !CRM_CATEGORIES.has(String(template.category || "").trim()));
    }
    return templates || [];
  }, [templateView, templates]);
  const counts = useMemo(() => {
    const crm = (templates || []).filter((template) => CRM_CATEGORIES.has(String(template.category || "").trim())).length;
    const documents = (templates || []).filter((template) => DOCUMENT_CATEGORIES.has(String(template.category || "").trim())).length;
    return { all: (templates || []).length, crm, documents };
  }, [templates]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Email Templates
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Create reusable templates that Elite Core can use anywhere an email is sent.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
          <Button variant="outlined" onClick={openCreateCrm}>
            Create CRM Template
          </Button>
          <Button variant="contained" onClick={openCreate}>
            Create Template
          </Button>
        </Stack>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        CRM templates are used for CRM draft previews. Live sending is not enabled yet.
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}>
        <Tabs value={templateView} onChange={(_event, value) => setTemplateView(value)}>
          <Tab value="all" label={`All Templates (${counts.all})`} />
          <Tab value="documents" label={`Document Templates (${counts.documents})`} />
          <Tab value="crm" label={`CRM Templates (${counts.crm})`} />
        </Tabs>
      </Box>

      {templateView === "crm" ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Use CRM templates from a CRM card to preview, copy, or save internal email drafts. They do not send email.
          </Typography>
        </Box>
      ) : null}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Key</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTemplates.map((template) => (
            <TableRow key={template.id} hover>
              <TableCell sx={{ fontWeight: 700 }}>{template.name}</TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{template.key}</TableCell>
              <TableCell>{template.category || "—"}</TableCell>
              <TableCell>{template.enabled ? "Yes" : "No"}</TableCell>
              <TableCell>
                {template.updatedAt ? new Date(template.updatedAt).toLocaleString() : "—"}
              </TableCell>
              <TableCell align="right">
                <IconButton onClick={() => openEdit(template)} size="small">
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => duplicate(template)} size="small">
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => archive(template)}
                  size="small"
                  disabled={template.isSystem}
                >
                  <ArchiveOutlinedIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!loading && filteredTemplates.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                {templateView === "crm"
                  ? "No CRM templates yet. Create a CRM template to support CRM draft previews."
                  : templateView === "documents"
                    ? "No document templates found."
                    : "No templates yet. Create your first one."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {draft.id ? "Edit Template" : "Create Template"}
        </DialogTitle>

        <DialogContent sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, pt: 1 }}>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <TextField
              label="Key (stable identifier)"
              value={draft.key}
              onChange={(e) => setDraft((current) => ({ ...current, key: e.target.value }))}
              helperText='Example: "sales-order-signed" or "new-po"'
              fullWidth
            />
            <TextField
              label="Name"
              value={draft.name}
              onChange={(e) => setDraft((current) => ({ ...current, name: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              SelectProps={{ native: true }}
              label="Category (optional)"
              value={draft.category}
              onChange={(e) => {
                const category = e.target.value;
                if (CRM_CATEGORIES.has(category)) {
                  selectCrmCategory(category);
                } else {
                  setDraft((current) => ({ ...current, category }));
                }
              }}
              fullWidth
            >
              <option value="">Uncategorized</option>
              {CATEGORY_OPTIONS.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </TextField>
            {isCrmTemplate ? (
              <TextField
                select
                SelectProps={{ native: true }}
                label="CRM template type"
                value={draft.category}
                onChange={(e) => selectCrmCategory(e.target.value)}
                fullWidth
              >
                {CRM_TYPE_OPTIONS.map((option) => (
                  <option key={option.category} value={option.category}>{option.label}</option>
                ))}
              </TextField>
            ) : null}
            {isCrmTemplate && !draft.id ? (
              <Box sx={{ display: "grid", gap: 1 }}>
                <TextField
                  select
                  SelectProps={{ native: true }}
                  label="Starter template"
                  value={starterChoice}
                  onChange={(e) => setStarterChoice(e.target.value)}
                  fullWidth
                >
                  <option value="blank">Start from blank</option>
                  <option value="starter">Use starter content</option>
                </TextField>
                {starterChoice === "starter" ? (
                  <Button variant="outlined" onClick={applyStarterContent} sx={{ justifySelf: "start" }}>
                    Apply Starter Content
                  </Button>
                ) : null}
              </Box>
            ) : null}
            {isCrmTemplate ? (
              <Box sx={{ p: 1.5, border: "1px dashed #ddd", borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: "block" }}>
                    CRM merge fields
                  </Typography>
                  <Chip size="small" label="Sample preview data" variant="outlined" />
                </Stack>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 0.75 }}>
                  {CRM_MERGE_FIELDS.map((field) => (
                    <Box key={field.key} sx={{ border: "1px solid #eee", borderRadius: 1, p: 0.75, background: "#fafafa" }}>
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 800 }}>
                        {field.label}
                      </Typography>
                      <Box component="code" sx={{ display: "block", fontSize: 12, mb: 0.75 }}>
                        {field.example}
                      </Box>
                      <Stack direction="row" spacing={0.75}>
                        <Button size="small" variant="outlined" onClick={() => insertMergeField(field.key, "subject")}>Subject</Button>
                        <Button size="small" variant="outlined" onClick={() => insertMergeField(field.key, "body")}>Body</Button>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Switch
                checked={!!draft.enabled}
                onChange={(e) => setDraft((current) => ({ ...current, enabled: e.target.checked }))}
              />
              <Typography sx={{ fontWeight: 700 }}>Enabled</Typography>
            </Box>

            <TextField
              label="Subject HTML"
              value={draft.subjectHtml}
              onChange={(e) => setDraft((current) => ({ ...current, subjectHtml: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              placeholder="Example: <b>Order Signed</b> — {{order.number}}"
            />

            <TextField
              label="Body HTML"
              value={draft.bodyHtml}
              onChange={(e) => setDraft((current) => ({ ...current, bodyHtml: e.target.value }))}
              fullWidth
              multiline
              minRows={10}
              placeholder="Example: <p>Hi {{customer.name}},</p><p>Your order {{order.number}} was signed.</p>"
            />
          </Box>

          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Preview
            </Typography>

            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {isCrmTemplate
                  ? "Preview uses sample CRM data only. Real CRM cards are merged from the card workspace."
                  : "Preview uses sample document data only. Real values are merged by the email workflow."}
              </Typography>

              {(isCrmTemplate ? CRM_MERGE_FIELDS.map((field) => field.key) : ["customer.name", "order.number", "user.name"]).map((key) => (
                <TextField
                  key={key}
                  label={key}
                  value={sampleVars[key]}
                  onChange={(e) =>
                    setSampleVars((current) => ({ ...current, [key]: e.target.value }))
                  }
                  size="small"
                  fullWidth
                />
              ))}
            </Box>

            <Box sx={{ mt: 1, p: 1.5, border: "1px solid #e5e5e5", borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Rendered Subject
              </Typography>
              <Box
                sx={{ p: 1, borderRadius: 1, background: "#fafafa" }}
                dangerouslySetInnerHTML={{ __html: renderedSubject || "<i>(empty)</i>" }}
              />
            </Box>

            <Box sx={{ p: 1.5, border: "1px solid #e5e5e5", borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Rendered Body
              </Typography>
              <Box
                sx={{ p: 1, borderRadius: 1, background: "#fafafa", minHeight: 220 }}
                dangerouslySetInnerHTML={{ __html: renderedBody || "<i>(empty)</i>" }}
              />
            </Box>

            <Box sx={{ p: 1.5, border: "1px dashed #ddd", borderRadius: 2 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {isCrmTemplate ? (
                  <>CRM examples: <code>{"{{customerName}} {{companyName}} {{cardTitle}} {{crmStage}} {{ownerName}} {{nextActivityDate}} {{trackingReference}}"}</code></>
                ) : (
                  <>Document examples: <code>{"{{customer.name}} {{order.number}} {{user.name}}"}</code></>
                )}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!draft.key.trim() || !draft.name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
